import { apiClient } from '@/utils/api-client';
import type {
  DrawdownBatch,
  DrawdownBatchCreate,
  DrawdownBatchUpdate,
  DrawdownBatchUpdateResponse,
  BatchPreviewResponse,
  DrawdownRecalculationRequest,
  DrawdownRecalculationResponse,
} from '@/types/drawdown';

/**
 * Triggers a browser download from a blob
 * @param blob - The blob to download
 * @param filename - The name for the downloaded file
 */
function downloadFile(blob: Blob, filename: string): void {
  try {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
  }
}

/**
 * Service for drawdown batch operations
 *
 * Handles batch CRUD, PDF generation, email sending, and recalculation
 */
export const DrawdownBatchService = {
  /**
   * Get all batches for a specific drawdown
   */
  async getBatches(
    fundId: string,
    drawdownId: string
  ): Promise<DrawdownBatch[]> {
    return await apiClient.get<DrawdownBatch[]>(
      `/funds/${fundId}/drawdowns/${drawdownId}/batches`
    );
  },

  /**
   * Get a specific batch by ID
   */
  async getBatch(
    fundId: string,
    drawdownId: string,
    batchId: string
  ): Promise<DrawdownBatch> {
    return await apiClient.get<DrawdownBatch>(
      `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}`
    );
  },

  /**
   * Create a new batch
   */
  async createBatch(
    fundId: string,
    drawdownId: string,
    data: DrawdownBatchCreate
  ): Promise<DrawdownBatch> {
    return await apiClient.post<DrawdownBatch>(
      `/funds/${fundId}/drawdowns/${drawdownId}/batches`,
      data
    );
  },

  /**
   * Update a batch (DRAFT only)
   */
  async updateBatch(
    fundId: string,
    drawdownId: string,
    batchId: string,
    data: DrawdownBatchUpdate
  ): Promise<DrawdownBatch> {
    return await apiClient.put<DrawdownBatch>(
      `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}`,
      data
    );
  },

  /**
   * Update batch status
   */
  async updateBatchStatus(
    fundId: string,
    drawdownId: string,
    batchId: string,
    status: string
  ): Promise<DrawdownBatch> {
    return await apiClient.patch<DrawdownBatch>(
      `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}/status?new_status=${encodeURIComponent(status)}`,
      null
    );
  },

  /**
   * Issue a single DRAFT batch and queue its notice PDFs.
   * For batches added after the drawdown was already issued.
   */
  async issueBatch(
    fundId: string,
    drawdownId: string,
    batchId: string
  ): Promise<DrawdownBatchUpdateResponse> {
    return await apiClient.post<DrawdownBatchUpdateResponse>(
      `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}/issue`,
      null
    );
  },

  /**
   * Generate PDFs for all investors in a batch
   */
  async generateBatchPDFs(
    fundId: string,
    drawdownId: string,
    batchId: string,
    regenerate: boolean = false
  ): Promise<{ message: string; batch_id: string; drawdown_id: string }> {
    const url = `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}/pdfs${regenerate ? '?regenerate=true' : ''}`;
    return await apiClient.post(url, null);
  },

  /**
   * Send email notifications to investors in a batch
   */
  async sendBatchEmails(
    fundId: string,
    drawdownId: string,
    batchId: string,
    newOnly: boolean = false
  ): Promise<{
    message: string;
    batch_id: string;
    drawdown_id: string;
    new_only: boolean;
  }> {
    const url = `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}/emails${newOnly ? '?new_only=true' : ''}`;
    return await apiClient.post(url, null);
  },

  /**
   * Get batch preview with investor notices
   */
  async getBatchPreview(
    fundId: string,
    drawdownId: string,
    batchId: string
  ): Promise<BatchPreviewResponse> {
    return await apiClient.get<BatchPreviewResponse>(
      `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}/preview`
    );
  },

  /**
   * Recalculate a drawdown by adding investors and creating new batches
   */
  async recalculateDrawdown(
    fundId: string,
    drawdownId: string,
    data: DrawdownRecalculationRequest
  ): Promise<DrawdownRecalculationResponse> {
    return await apiClient.post<DrawdownRecalculationResponse>(
      `/funds/${fundId}/drawdowns/${drawdownId}/recalculate`,
      data
    );
  },

  /**
   * Get available investors who can be added to a drawdown
   * Returns investors from the fund who are not yet in the drawdown
   */
  async getAvailableInvestors(
    fundId: string,
    drawdownId: string
  ): Promise<
    Array<{
      investor_id: string;
      investor_name: string;
      commitment_amount: number;
    }>
  > {
    return await apiClient.get<
      Array<{
        investor_id: string;
        investor_name: string;
        commitment_amount: number;
      }>
    >(`/funds/${fundId}/drawdowns/${drawdownId}/available-investors`);
  },

  /**
   * Download PDF by notification ID using document system
   */
  async downloadInvestorPdfByNotification(
    fundId: string,
    drawdownId: string,
    batchId: string,
    notificationId: string
  ): Promise<void> {
    // Get document download URL from notification
    const response = await apiClient.get<{
      download_url: string;
      expires_in: number;
      document_id: string;
    }>(
      `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}/notifications/${notificationId}/document-url`
    );

    if (!response?.download_url) {
      throw new Error('Download URL not available');
    }

    // Fetch the presigned URL as blob and trigger download
    const fetchResponse = await fetch(response.download_url);
    if (!fetchResponse.ok) {
      throw new Error(`Failed to fetch document: ${fetchResponse.statusText}`);
    }
    const blob = await fetchResponse.blob();
    downloadFile(blob, `capital-call-${notificationId}.pdf`);
  },

  /**
   * Preview notification PDF for a specific investor with watermark
   * Opens the preview PDF in a new browser tab
   */
  async previewInvestorNotification(
    fundId: string,
    drawdownId: string,
    batchId: string,
    investorId: string
  ): Promise<void> {
    const url = `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}/investors/${investorId}/pdf/preview`;
    const blob = await apiClient.getBlob(url);

    // Open in new tab
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');

    // Cleanup after a delay to ensure the tab has loaded
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },

  /**
   * Download all PDFs for a batch as ZIP
   */
  async downloadBatchPdfsZip(
    fundId: string,
    drawdownId: string,
    batchId: string
  ): Promise<void> {
    const url = `/funds/${fundId}/drawdowns/${drawdownId}/batches/${batchId}/pdfs/zip`;
    const blob = await apiClient.getBlob(url);
    downloadFile(blob, `drawdown-${drawdownId}-batch-${batchId}-pdfs.zip`);
  },
};
